import { escapeHtml, stripTags } from '@eliadmin/core/src/utils/dlz/text';

const ALLOWED_TAGS = new Set(['P', 'BR', 'B', 'STRONG', 'I', 'EM', 'U', 'UL', 'OL', 'LI', 'A']);
const DROPPED_WITH_CONTENT = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'TEMPLATE']);
const SAFE_HREF = /^(https?:|mailto:)/i;

/** Plain-text reviews carry no markup or entities; anything else goes through the sanitizer. */
export const looksLikeHtml = (text: string) => /<[a-z!/]|&(#\d+|[a-z]+);/i.test(text);

function copyAllowed(source: Node, target: Node) {
  source.childNodes.forEach(child => {
    if (child.nodeType === Node.TEXT_NODE) {
      target.appendChild(document.createTextNode(child.textContent ?? ''));
      return;
    }
    if (!(child instanceof Element) || DROPPED_WITH_CONTENT.has(child.tagName)) return;
    if (!ALLOWED_TAGS.has(child.tagName)) {
      copyAllowed(child, target);
      return;
    }
    const element = document.createElement(child.tagName);
    const href = child.getAttribute('href')?.trim();
    if (child.tagName === 'A' && href && SAFE_HREF.test(href)) {
      element.setAttribute('href', href);
      element.setAttribute('rel', 'nofollow noopener noreferrer ugc');
      element.setAttribute('target', '_blank');
    }
    copyAllowed(child, element);
    target.appendChild(element);
  });
}

/**
 * Customer-written HTML reduced to an allow-list (inline formatting, lists, http(s)/mailto links);
 * unlike admin content it never keeps attributes, scripts or styles.
 */
export function sanitizeReviewHtml(html: string) {
  // Server render has no DOM: fall back to escaped plain text, the client renders the real markup.
  if (typeof DOMParser === 'undefined') return escapeHtml(stripTags(html));
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const container = document.createElement('div');
  copyAllowed(parsed.body, container);
  return container.innerHTML;
}
